import type {
  AdminDocumentPackageDetail,
  AdminLegalSignatureEvidence,
} from '../legalTypes';

export const DocumentLifecycleStates = [
  'needs_input',
  'queued',
  'generating',
  'generation_failed',
  'ready_for_review',
  'approved',
  'sent',
  'partially_executed',
  'executed',
  'declined',
  'voided',
  'superseded',
  'archived',
] as const;

export type DocumentLifecycleState = typeof DocumentLifecycleStates[number];

export interface DocumentStatePresentation {
  label: string;
  marker: string;
  description: string;
  tone: 'neutral' | 'info' | 'warning' | 'success' | 'danger';
}

const StatePresentation: Record<DocumentLifecycleState, DocumentStatePresentation> = {
  needs_input: {
    label: 'Needs input', marker: '[!]', tone: 'warning',
    description: 'Required legal or package inputs must be completed before generation can start.',
  },
  queued: {
    label: 'Queued', marker: '[…]', tone: 'info',
    description: 'Generation is queued and waiting for an available document worker.',
  },
  generating: {
    label: 'Generating', marker: '[~]', tone: 'info',
    description: 'Immutable document artifacts are being rendered from the frozen package snapshot.',
  },
  generation_failed: {
    label: 'Generation failed', marker: '[×]', tone: 'danger',
    description: 'Generation reached a terminal failure and requires an explicit, eligible retry.',
  },
  ready_for_review: {
    label: 'Ready for review', marker: '[R]', tone: 'info',
    description: 'Generated artifacts are immutable and awaiting an authorized review decision.',
  },
  approved: {
    label: 'Approved', marker: '[✓]', tone: 'success',
    description: 'The reviewed package is approved and may be delivered when all send gates pass.',
  },
  sent: {
    label: 'Sent', marker: '[→]', tone: 'info',
    description: 'The stored package was delivered to its frozen recipient plan and awaits evidence.',
  },
  partially_executed: {
    label: 'Partially executed', marker: '[½]', tone: 'warning',
    description: 'Valid evidence exists for some, but not all, required package signatories.',
  },
  executed: {
    label: 'Executed', marker: '[✓✓]', tone: 'success',
    description: 'Every required signatory has verified evidence; this version is immutable history.',
  },
  declined: {
    label: 'Declined', marker: '[−]', tone: 'danger',
    description: 'Execution was declined and the package remains retained as immutable legal history.',
  },
  voided: {
    label: 'Voided', marker: '[Ø]', tone: 'danger',
    description: 'The package was irreversibly voided and cannot return to an active lifecycle state.',
  },
  superseded: {
    label: 'Superseded', marker: '[S]', tone: 'neutral',
    description: 'A newer lineage version replaced this package; this version remains read-only history.',
  },
  archived: {
    label: 'Archived', marker: '[A]', tone: 'neutral',
    description: 'The package is hidden from active work while all legal metadata remains retained.',
  },
};

export const getDocumentStatePresentation = (state: string): DocumentStatePresentation =>
  StatePresentation[state as DocumentLifecycleState] ?? {
    label: state.replace(/_/g, ' ') || 'Unknown state',
    marker: '[?]',
    tone: 'neutral',
    description: 'The server returned a lifecycle state this client does not yet recognise.',
  };

export type DocumentUiCommand =
  | 'complete_missing_input'
  | 'generate'
  | 'retry_generation'
  | 'regenerate_before_approval'
  | 'approve'
  | 'amend'
  | 'void'
  | 'archive'
  | 'restore'
  | 'send'
  | 'resend'
  | 'revoke'
  | 'upload_signed'
  | 'complete_execution'
  | 'decline';

export interface DocumentCommandPresentation {
  label: string;
  endpointCommand: string;
  effect: string;
  destructive: boolean;
  confirmLabel?: string;
}

const CommandPresentation: Record<DocumentUiCommand, DocumentCommandPresentation> = {
  complete_missing_input: {
    label: 'Complete missing input', endpointCommand: 'input', destructive: false,
    effect: 'Save the required legal and processing facts before document generation is queued.',
  },
  generate: {
    label: 'Queue generation', endpointCommand: 'generate', destructive: false,
    effect: 'Queue immutable package generation from the current validated input snapshot.',
  },
  retry_generation: {
    label: 'Retry generation', endpointCommand: 'generate', destructive: false,
    effect: 'Queue a new generation attempt without changing the frozen business inputs.',
  },
  regenerate_before_approval: {
    label: 'Regenerate before approval', endpointCommand: 'regenerate', destructive: false,
    effect: 'Create a fresh rendered version before approval; previously generated metadata remains retained.',
  },
  approve: {
    label: 'Approve package', endpointCommand: 'approve', destructive: false,
    effect: 'Approve the reviewed immutable artifacts and make the package eligible for delivery.',
  },
  amend: {
    label: 'Create amendment', endpointCommand: 'amend', destructive: false,
    effect: 'Supersede this approved package with a lineage-linked package using current profile facts.',
  },
  void: {
    label: 'Void package', endpointCommand: 'void', destructive: true,
    confirmLabel: 'Void package permanently',
    effect: 'Permanently void this package while retaining its immutable versions and complete audit history.',
  },
  archive: {
    label: 'Archive package', endpointCommand: 'archive', destructive: true,
    confirmLabel: 'Archive retained package',
    effect: 'Remove this terminal package from active work while retaining all legal metadata and evidence.',
  },
  restore: {
    label: 'Unarchive package', endpointCommand: 'restore', destructive: false,
    effect: 'Unarchive retained package metadata and return it to the server-defined active state; immutable versions remain unchanged.',
  },
  send: {
    label: 'Send package', endpointCommand: 'send', destructive: false,
    effect: 'Deliver the approved stored documents to the frozen recipient plan without regenerating them.',
  },
  resend: {
    label: 'Resend package', endpointCommand: 'resend', destructive: false,
    effect: 'Deliver the same stored document to the selected recipient again without changing its version.',
  },
  revoke: {
    label: 'Revoke delivery', endpointCommand: 'revoke', destructive: true,
    confirmLabel: 'Revoke recipient delivery',
    effect: 'Revoke the selected recipient delivery token while preserving all prior delivery evidence.',
  },
  upload_signed: {
    label: 'Upload returned signature', endpointCommand: 'signed-artifact', destructive: false,
    effect: 'Submit a returned signed artifact for safe scanning and immutable evidence recording.',
  },
  complete_execution: {
    label: 'Record execution', endpointCommand: 'complete', destructive: true,
    confirmLabel: 'Record execution permanently',
    effect: 'Permanently mark the selected signer complete using already recorded verified evidence.',
  },
  decline: {
    label: 'Decline package', endpointCommand: 'decline', destructive: true,
    confirmLabel: 'Decline package permanently',
    effect: 'Permanently close execution as declined while retaining delivery and evidence history.',
  },
};

export const getDocumentCommandPresentation = (
  command: DocumentUiCommand
): DocumentCommandPresentation => CommandPresentation[command];

const signerInPackage = (detail: AdminDocumentPackageDetail, signerId: string) =>
  detail.signers.find((signer) => signer.id === signerId);

const executionOpen = (detail: AdminDocumentPackageDetail): boolean =>
  detail.state === 'sent' || detail.state === 'partially_executed';

export const canConfigureDocumentSigners = (detail: AdminDocumentPackageDetail): boolean =>
  detail.state === 'approved';

export const canRunDocumentSignerDelivery = (
  detail: AdminDocumentPackageDetail,
  signerId: string
): boolean => executionOpen(detail) && signerInPackage(detail, signerId)?.state === 'sent';

export const canUploadDocumentEvidence = (
  detail: AdminDocumentPackageDetail,
  signerId: string
): boolean => executionOpen(detail) && signerInPackage(detail, signerId)?.state === 'sent';

export const canCompleteDocumentSigner = (
  detail: AdminDocumentPackageDetail,
  signerId: string,
  evidence: ReadonlyArray<AdminLegalSignatureEvidence> | null
): boolean => canUploadDocumentEvidence(detail, signerId)
  && evidence !== null
  && evidence.some((record) =>
    record.signerId === signerId
    && record.verificationStatus.toLocaleLowerCase() === 'verified');
